import { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
  }

  private handleRefresh = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = '/';
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-950 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-900 dark:ring-1 dark:ring-gray-700/50 rounded-2xl shadow-xl dark:shadow-black/40 max-w-md w-full p-8 text-center">
            {/* Icon */}
            <div className="w-16 h-16 bg-red-100 dark:bg-red-500/15 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="w-8 h-8 text-red-500 dark:text-red-400" />
            </div>

            {/* Title */}
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-50 mb-2">
              Something went wrong
            </h1>

            {/* Description */}
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              We're sorry, but something unexpected happened. Please try refreshing the page or go back to the home screen.
            </p>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={this.handleRefresh}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 text-white rounded-xl font-medium hover:bg-primary-600 transition-colors"
              >
                <RefreshCw size={18} />
                Refresh Page
              </button>
              <button
                onClick={this.handleGoHome}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 rounded-xl font-medium hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              >
                <Home size={18} />
                Go Home
              </button>
            </div>

            {/* Help text */}
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-6">
              If this problem persists, please{' '}
              <a
                href="mailto:support@coordal.com"
                className="text-primary-500 hover:text-primary-600 underline"
              >
                contact support
              </a>.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * User-friendly error display component for API errors
 */
interface ErrorDisplayProps {
  title?: string;
  message?: string;
  onRetry?: () => void;
  onDismiss?: () => void;
}

export function ErrorDisplay({
  title = 'Unable to load',
  message = 'Something went wrong. Please try again.',
  onRetry,
  onDismiss,
}: ErrorDisplayProps) {
  return (
    <div className="bg-red-50 dark:bg-red-500/10 border border-red-100 dark:border-red-500/20 rounded-xl p-4">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 bg-red-100 dark:bg-red-500/15 rounded-full flex items-center justify-center flex-shrink-0">
          <AlertTriangle className="w-5 h-5 text-red-500 dark:text-red-400" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-red-800 dark:text-red-300">{title}</h3>
          <p className="text-sm text-red-600 dark:text-red-400/90 mt-1">{message}</p>
          {(onRetry || onDismiss) && (
            <div className="flex gap-2 mt-3">
              {onRetry && (
                <button
                  onClick={onRetry}
                  className="text-sm font-medium text-red-700 hover:text-red-800 flex items-center gap-1"
                >
                  <RefreshCw size={14} />
                  Try again
                </button>
              )}
              {onDismiss && (
                <button
                  onClick={onDismiss}
                  className="text-sm font-medium text-gray-500 hover:text-gray-700"
                >
                  Dismiss
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Convert API error to user-friendly message
 */
export function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    // Handle common API errors
    const message = error.message.toLowerCase();

    if (message.includes('network') || message.includes('fetch')) {
      return 'Unable to connect. Please check your internet connection.';
    }
    if (message.includes('401') || message.includes('unauthorized')) {
      return 'Please sign in to continue.';
    }
    if (message.includes('403') || message.includes('forbidden')) {
      return 'You don\'t have permission to access this.';
    }
    if (message.includes('404') || message.includes('not found')) {
      return 'The requested item could not be found.';
    }
    if (message.includes('500') || message.includes('server')) {
      return 'Our servers are having issues. Please try again later.';
    }
    if (message.includes('timeout')) {
      return 'The request took too long. Please try again.';
    }
    if (message.includes('mapbox')) {
      return 'Map service is temporarily unavailable. Please try again.';
    }
  }

  return 'Something went wrong. Please try again.';
}
